import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isPasswordPwned } from '../../lib/passwordSecurity';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ResetPasswordPage() {
  // Base path pour les URLs (gère les déploiements sur sous-chemin)
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';

  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const type = searchParams.get('type');

  useEffect(() => {
    // Vérifier le token de recovery au chargement de la page
    const verifyToken = async () => {
      if (!token || type !== 'recovery') {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        console.log('🔐 Vérification du token de recovery...');
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery',
        });

        if (verifyError) {
          console.error('❌ Token invalide:', verifyError.message);
          setVerifying(false);
          setTokenValid(false);
          return;
        }

        console.log('✅ Token vérifié, session établie');
        setVerifying(false);
        setTokenValid(true);

        // Nettoyer l'URL : retirer les query params pour éviter de les réutiliser
        window.history.replaceState({}, '', `${pathPrefix}/reset-password`);
      } catch (err: any) {
        console.error('❌ Erreur vérification token:', err);
        setVerifying(false);
        setTokenValid(false);
      }
    };

    verifyToken();
  }, [token, type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    // Vérification anti-fuite : refuser les mots de passe compromis (HaveIBeenPwned)
    const pwned = await isPasswordPwned(password);
    if (pwned) {
      setError('Ce mot de passe a été compromis lors d\'une fuite de données. Pour votre sécurité, choisissez-en un autre.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        throw updateError;
      }

      console.log('✅ Mot de passe mis à jour avec succès');
      setSuccess(true);

      // Rediriger vers la page de connexion après un court délai
      setTimeout(() => {
        // Déconnecter pour forcer une nouvelle connexion avec le nouveau mot de passe
        supabase.auth.signOut().then(() => {
          navigate('/login', { replace: true });
        });
      }, 2500);
    } catch (err: any) {
      console.error('❌ Erreur mise à jour mot de passe:', err);
      setError(err.message || 'Erreur lors de la réinitialisation du mot de passe. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-password-line text-3xl text-clinic-blue"></i>
              </div>
              <h2 className="text-3xl font-bold text-clinic-blue-dark">Nouveau mot de passe</h2>
              <p className="text-gray-600 mt-2">
                {verifying
                  ? 'Vérification de votre lien...'
                  : tokenValid
                    ? 'Créez votre nouveau mot de passe'
                    : 'Lien de réinitialisation invalide ou expiré'}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-clinic-green/10 border-2 border-clinic-green/30 text-clinic-green rounded-2xl text-sm">
                Mot de passe réinitialisé avec succès ! Redirection vers la connexion...
              </div>
            )}

            {/* État : vérification en cours */}
            {verifying && (
              <div className="text-center py-8">
                <div className="animate-spin w-10 h-10 border-4 border-clinic-blue/20 border-t-clinic-blue rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600 text-sm">Vérification de votre lien de réinitialisation...</p>
              </div>
            )}

            {/* État : token invalide ou expiré */}
            {!verifying && !tokenValid && !success && (
              <div className="text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-close-circle-line text-3xl text-red-500"></i>
                </div>
                <p className="text-gray-600 mb-2">Ce lien n'est plus valide. Il a peut-être expiré ou déjà été utilisé.</p>
                <p className="text-gray-500 text-sm mb-6">Veuillez faire une nouvelle demande de réinitialisation.</p>
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Retour à la connexion
                </button>
              </div>
            )}

            {/* État : token valide, formulaire de nouveau mot de passe */}
            {!verifying && tokenValid && !success && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Nouveau mot de passe
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="Minimum 10 caractères"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Confirmer le mot de passe
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || success}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}